import HeroCanvas from "./HeroCanvas";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-end pb-20 md:pb-32 pt-32"
    >
      <HeroCanvas />

      {/* very subtle top/bottom fades — the glass panel does the real separation */}
      <div
        className="absolute inset-x-0 bottom-0 h-[20%] z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, var(--hero-fade-80), transparent)",
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-24 z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, var(--hero-fade-80), transparent)",
        }}
      />

      <div className="relative z-20 max-w-7xl mx-auto w-full px-6 md:px-12">
        {/* Wide glass-morphism panel — animation visible behind */}
        <div
          className="relative w-full border border-[var(--border-subtle)] dark:border-white/10 px-7 md:px-12 py-10 md:py-14 overflow-hidden"
          style={{
            background: "var(--hero-panel-bg)",
            backdropFilter: "blur(8px) saturate(115%)",
            WebkitBackdropFilter: "blur(8px) saturate(115%)",
          }}
        >
          {/* Grid backdrop overlay (like Metrics section) */}
          <div className="absolute inset-0 grid-backdrop opacity-50 pointer-events-none" />

          {/* Brand accent bar on the left edge */}
          <span
            aria-hidden
            className="absolute left-0 top-6 bottom-6 w-[2px] bg-[var(--color-teal)] dark:bg-[var(--color-yellow)]"
          />

          <div className="relative">
            <div className="flex items-center gap-3 mb-7">
              <span className="w-1.5 h-1.5 bg-[var(--color-yellow)] animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--color-accent-text)]">
                Project of Indo-Pacific Significance / Canberra · ACT
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-[var(--text)] text-[44px] sm:text-6xl lg:text-[80px] leading-[0.98] font-medium tracking-[-0.025em] max-w-5xl"
            >
              Creating a{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[var(--color-primary)] dark:text-[var(--color-yellow)]">
                  Sovereign
                </span>
                <span className="absolute -bottom-2 left-0 right-0 h-[6px] bg-[var(--color-yellow)]/30" />
              </span>{" "}
              Digital Future
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-7 max-w-2xl text-base md:text-lg text-[var(--text-70)] leading-relaxed"
            >
              Advancing Australia&apos;s technology leadership through
              zero-carbon, exascale-ready supercomputing — built for the
              AI era and the Indo-Pacific.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("solutions")}
                className="btn-primary"
              >
                Explore Infrastructure
                <span className="arrow">→</span>
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("contact")}
                className="btn-ghost"
              >
                Join the mission
              </button>
            </div>
          </div>
        </div>

        {/* spec strip — also full width, frosted */}
        <div
          className="mt-6 grid grid-cols-2 md:grid-cols-4 w-full border border-[var(--border-subtle)] dark:border-white/10 relative overflow-hidden"
          style={{
            background: "var(--hero-panel-bg)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div className="absolute inset-0 grid-backdrop opacity-50 pointer-events-none" />
          {[
            ["100%", "Renewable Grid"],
            ["EXA", "Scale Compute"],
            ["AU", "Sovereign Data"],
            ["INDO-PAC", "Regional Reach"],
          ].map(([k, v], i, arr) => (
            <div
              key={k}
              data-testid={`hero-stat-${k.toLowerCase()}`}
              className={`relative px-5 md:px-8 py-5 ${i < arr.length - 1 ? "md:border-r" : ""} border-[var(--border-subtle)] dark:border-white/10`}
            >
              <div className="font-display text-[var(--color-primary)] dark:text-[var(--color-lime)] text-2xl md:text-3xl tracking-tight">
                {k}
              </div>
              <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[var(--text-50)] mt-1">
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* scene indicator */}
      <div className="absolute top-28 right-6 md:right-12 z-20 hidden sm:flex flex-col items-end gap-3">
        <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[var(--text-40)]">
          LIVE / RT-LOOP
        </div>
        <div className="flex gap-1.5">
          <span className="w-6 h-px bg-[var(--color-yellow)]" />
          <span className="w-6 h-px bg-[var(--text-20)]" />
          <span className="w-6 h-px bg-[var(--text-20)]" />
          <span className="w-6 h-px bg-[var(--text-20)]" />
        </div>
      </div>
    </section>
  );
}
